import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { PAYOUT_METHOD_LABELS, PAYOUT_METHODS, type ManualClientDTO, type PayoutMethod } from '@bifeai/shared';
import { Badge, Button, Callout, Card, CardBody, Dialog, EmptyState, Field, Input, Select, Skeleton, useToast } from '@bifeai/ui';
import { CalendarClock, CircleCheck } from 'lucide-react';
import { useState } from 'react';
import { fieldsOf, parseAmount } from '../components/sellers';
import { api, errorMessage } from '../lib/api';
import { formatDate, formatUsd } from '../lib/format';
import { useCan } from '../lib/session';

const today = () => new Date(Date.now() - 3 * 3_600_000).toISOString().slice(0, 10);

function DueBadge({ days }: { days: number | null }) {
  if (days === null) return <Badge>Sin fecha</Badge>;
  if (days < 0)
    return (
      <Badge tone="danger">
        Vencido hace {-days} {days === -1 ? 'día' : 'días'}
      </Badge>
    );
  if (days === 0) return <Badge tone="danger">Vence hoy</Badge>;
  if (days <= 30)
    return (
      <Badge tone="warning">
        Vence en {days} {days === 1 ? 'día' : 'días'}
      </Badge>
    );
  return <Badge tone="success">Faltan {days} días</Badge>;
}

/**
 * Clientes con cobro manual (sin suscripción en Stripe): cuándo vence su mantenimiento y registrar la
 * renovación cuando pagan. Los avisos de vencimiento llegan solos a Avisos (30 y 7 días antes, el día y vencido).
 */
export function ManualClients() {
  const can = useCan();
  const [renewing, setRenewing] = useState<ManualClientDTO | null>(null);
  const qc = useQueryClient();
  const toast = useToast();
  const list = useQuery({ queryKey: ['tenants', 'manual'], queryFn: () => api.get<ManualClientDTO[]>('/platform/billing/manual') });
  const rows = list.data ?? [];
  return (
    <>
      <Callout>
        Clientes que pagan a mano (sin tarjeta en Stripe). Te avisamos en Avisos 30 y 7 días antes del vencimiento, el día que vence y cuando ya está
        vencido. Cuando paguen, registrá la renovación acá: se extiende el abono un año y se generan las comisiones que correspondan.
      </Callout>
      <div style={{ height: 14 }} />
      <Card>
        <CardBody flush>
          {list.isPending ? (
            <Skeleton style={{ height: 160 }} />
          ) : !rows.length ? (
            <EmptyState icon={<CalendarClock />} title="No hay clientes con cobro manual" />
          ) : (
            <div className="bf-table-wrap">
              <table className="bf-table">
                <thead>
                  <tr>
                    <th>Cliente</th>
                    <th>Vencimiento</th>
                    <th style={{ textAlign: 'right' }}>Mantenimiento</th>
                    <th>Último cobro</th>
                    <th>Términos</th>
                    {can('commissions.pay') && <th aria-label="Acciones" />}
                  </tr>
                </thead>
                <tbody>
                  {rows.map((c) => (
                    <tr key={c.tenantId}>
                      <td>
                        <span className="bf-cell-main">{c.name}</span>
                        <div className="bf-cell-sub">{[c.ownerEmail, c.contactPhone].filter(Boolean).join(' · ') || '—'}</div>
                        {(c.province || c.sellerName) && (
                          <div className="bf-cell-sub">
                            {[[c.locality, c.province].filter(Boolean).join(', '), c.sellerName && `Vendió ${c.sellerName}`]
                              .filter(Boolean)
                              .join(' · ')}
                          </div>
                        )}
                      </td>
                      <td>
                        <DueBadge days={c.daysLeft} />
                        {c.serviceUntil && <div className="bf-cell-sub">{formatDate(c.serviceUntil)}</div>}
                      </td>
                      <td className="bf-num" style={{ textAlign: 'right' }}>
                        {c.yearlyCents ? formatUsd(c.yearlyCents) : '—'}
                      </td>
                      <td className="bf-cell-sub">{c.lastPaidAt ? formatDate(c.lastPaidAt) : '—'}</td>
                      <td>{c.termsSigned ? <Badge tone="success">Firmados</Badge> : <Badge tone="warning">Sin firmar</Badge>}</td>
                      {can('commissions.pay') && (
                        <td style={{ textAlign: 'right' }}>
                          <Button size="sm" icon={<CircleCheck />} onClick={() => setRenewing(c)}>
                            Registrar renovación
                          </Button>
                        </td>
                      )}
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          )}
        </CardBody>
      </Card>
      {renewing && (
        <RenewalDialog
          c={renewing}
          onClose={() => setRenewing(null)}
          onDone={async (until) => {
            setRenewing(null);
            toast.success('Renovación registrada', `Abono hasta el ${formatDate(until)}.`);
            await qc.invalidateQueries({ queryKey: ['tenants'] });
            await qc.invalidateQueries({ queryKey: ['billing'] });
          }}
        />
      )}
    </>
  );
}

function RenewalDialog({ c, onClose, onDone }: { c: ManualClientDTO; onClose: () => void; onDone: (until: string) => Promise<void> }) {
  const [method, setMethod] = useState<PayoutMethod>('BANK');
  const [currency, setCurrency] = useState<'USD' | 'ARS'>('USD');
  const [amount, setAmount] = useState(c.yearlyCents ? String(c.yearlyCents / 100) : '');
  const [fx, setFx] = useState('');
  const [paidOn, setPaidOn] = useState(today());
  const [reference, setReference] = useState('');
  const [errors, setErrors] = useState<Record<string, string>>({});
  const [error, setError] = useState<string | null>(null);
  const n = parseAmount;
  const usdCents = currency === 'USD' ? Math.round(n(amount) * 100) : n(fx) > 0 ? Math.round((n(amount) * 100) / n(fx)) : 0;
  const save = useMutation({
    mutationFn: () =>
      api.post<{ serviceUntil: string }>(`/platform/billing/tenants/${c.tenantId}/manual-renewal`, {
        method,
        currency,
        amount: n(amount),
        fxRate: currency === 'ARS' ? n(fx) : null,
        paidOn,
        reference,
      }),
    onSuccess: (r) => onDone(r.serviceUntil),
    onError: (err) => {
      setErrors(fieldsOf(err));
      setError(errorMessage(err));
    },
  });
  return (
    <Dialog open onOpenChange={(o) => !o && onClose()} title={`Renovación de ${c.name}`}>
      <div className="bf-form">
        {error && <Callout tone="danger">{error}</Callout>}
        <Callout>
          Se extiende el abono un año desde el vencimiento{c.serviceUntil ? ` (${formatDate(c.serviceUntil)})` : ''}, o desde el día del pago si ya
          estaba vencido. Queda como venta y genera las comisiones de renovación si corresponden.
        </Callout>
        <div className="bf-form-row">
          <Field label="Cómo pagó">
            <Select value={method} onChange={(e) => setMethod(e.target.value as PayoutMethod)}>
              {PAYOUT_METHODS.map((m) => (
                <option key={m} value={m}>
                  {PAYOUT_METHOD_LABELS[m]}
                </option>
              ))}
            </Select>
          </Field>
          <Field label="Fecha del cobro" error={errors.paidOn}>
            <Input type="date" value={paidOn} onChange={(e) => setPaidOn(e.target.value)} />
          </Field>
        </div>
        <div className="bf-form-row">
          <Field label="Moneda">
            <Select value={currency} onChange={(e) => setCurrency(e.target.value as 'USD' | 'ARS')}>
              <option value="USD">Dólares</option>
              <option value="ARS">Pesos</option>
            </Select>
          </Field>
          <Field label={currency === 'USD' ? 'Monto (USD)' : 'Monto (pesos)'} error={errors.amount}>
            <Input inputMode="decimal" value={amount} onChange={(e) => setAmount(e.target.value)} />
          </Field>
          {currency === 'ARS' && (
            <Field label="Dólar de ese día" hint="Pesos por dólar" error={errors.fxRate}>
              <Input inputMode="decimal" value={fx} onChange={(e) => setFx(e.target.value)} />
            </Field>
          )}
        </div>
        <div className="bf-cell-sub">
          Se registra como <strong>{formatUsd(usdCents)}</strong>.
        </div>
        <Field label="Comprobante o referencia" hint="N° de operación, hash de la transacción…">
          <Input value={reference} onChange={(e) => setReference(e.target.value)} maxLength={200} />
        </Field>
        <div className="bf-form-actions">
          <Button onClick={onClose}>Cancelar</Button>
          <Button variant="primary" loading={save.isPending} disabled={usdCents <= 0} onClick={() => save.mutate()}>
            Registrar renovación
          </Button>
        </div>
      </div>
    </Dialog>
  );
}
